import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "zc3d_session_id";
const PING_INTERVAL_MS = 15000;
const COUNT_INTERVAL_MS = 10000;

function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    // Cheap UUID v4 (avoids importing crypto polyfills).
    sid = "s-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

// React hook that returns the current concurrent-player count.
// • When `active === true`, the client also POSTs heartbeats — this is what
//   makes the user be COUNTED as a player. Set to true only while the user
//   is actually in a level (per product decision).
// • `game` is sent with each ping so the backend can group by-game. Defaults
//   to "zombie-collector-3d" for backwards compatibility with old clients.
// • The count is fetched on mount and every 10s thereafter.
// • If the backend is unreachable, returns null and the UI falls back to "1".
export function usePresence(active, game = "zombie-collector-3d") {
  const [count, setCount] = useState(null);
  const sessionRef = useRef(getSessionId());

  // Heartbeat while active
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const ping = async () => {
      try {
        await axios.post(`${API_BASE}/presence/ping`, {
          session_id: sessionRef.current,
          game,
        });
      } catch {
        // ignore — counter will fall back
      }
    };
    ping();
    const id = setInterval(() => {
      if (!cancelled) ping();
    }, PING_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [active, game]);

  // Poll the count
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API_BASE}/presence/count`);
        if (!cancelled && typeof res?.data?.count === "number") {
          setCount(res.data.count);
        }
      } catch {
        if (!cancelled) setCount((c) => (c === null ? null : c));
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, COUNT_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return count;
}

// Hook used on the arcade homepage. Polls /api/presence/by-game every 12s
// and returns { games: { 'zombie-collector-3d': N, ... }, total: M }.
// Does NOT ping — pure read. Falls back to {games:{}, total:0} on error.
const BY_GAME_POLL_MS = 12000;

export function usePresenceByGame() {
  const [data, setData] = useState({ games: {}, total: 0 });
  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      try {
        const res = await axios.get(`${API_BASE}/presence/by-game`);
        if (!cancelled && res?.data) {
          setData({
            games: res.data.games || {},
            total: res.data.total || 0,
          });
        }
      } catch {
        // ignore
      }
    };
    fetchIt();
    const id = setInterval(fetchIt, BY_GAME_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return data;
}
